import API_BASE_URL from "../services/api.js";
import config from "../config/env.js";
import tokenManager from "./tokenManager.js";

// Country data
// Countries that can be selected when entering a shipping address.
// The list mirrors the set of shipping destinations that are enabled in
// our Stripe configuration so that customers can select any country that
// Stripe can charge shipping for.  Keep the codes ISO-3166-1 alpha-2 so
// that we can pass them straight through to Stripe when creating a
// Checkout Session.
export const COUNTRIES = [
  { code: "AR", name: "Argentina" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colombia" },
  { code: "DK", name: "Denmark" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LU", name: "Luxembourg" },
  { code: "MC", name: "Monaco" },
  { code: "NL", name: "Netherlands" },
  { code: "NO", name: "Norway" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "KR", name: "South Korea" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "TH", name: "Thailand" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "VN", name: "Vietnam" },
];

// US States
export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// Canadian Provinces
export const CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

// UK Counties
export const UK_COUNTIES = [
  { code: "BKM", name: "Buckinghamshire" },
  { code: "CAM", name: "Cambridgeshire" },
  { code: "CHS", name: "Cheshire" },
  { code: "DBY", name: "Derbyshire" },
  { code: "DEV", name: "Devon" },
  // Add more UK counties as needed
];

// Australian States
export const AU_STATES = [
  { code: "NSW", name: "New South Wales" },
  { code: "QLD", name: "Queensland" },
  { code: "SA", name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "VIC", name: "Victoria" },
  { code: "WA", name: "Western Australia" },
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NT", name: "Northern Territory" },
];

// NZ Regions
export const NZ_REGIONS = [
  { code: "AUK", name: "Auckland" },
  { code: "BOP", name: "Bay of Plenty" },
  { code: "CAN", name: "Canterbury" },
  { code: "WGN", name: "Wellington" },
  // Add more NZ regions as needed
];

// Validation functions
export const validateStreetAddress = (street) => {
  // Enhanced street address validation
  // Must contain:
  // - At least one number
  // - Letters
  // - Optional unit/apt/suite numbers
  // - Common street types (St, Ave, Rd, etc.)
  // - Allow special characters for unit numbers (#, -, /, etc.)
  const hasNumber = /\d+/.test(street);
  const hasLetters = /[a-zA-Z]+/.test(street);
  const validFormat = /^[a-zA-Z0-9\s#.,/&'"()-]+$/i.test(street);
  const minLength = street.length >= 5;
  const commonWords =
    /(street|st|avenue|ave|road|rd|drive|dr|lane|ln|way|circle|cir|court|ct|boulevard|blvd|place|pl|square|sq|suite|ste|apartment|apt|unit|#)/i.test(
      street,
    );

  if (!hasNumber)
    return { isValid: false, error: "Address must include a street number" };
  if (!hasLetters)
    return { isValid: false, error: "Address must include street name" };
  if (!validFormat)
    return { isValid: false, error: "Address contains invalid characters" };
  if (!minLength) return { isValid: false, error: "Address is too short" };
  if (!commonWords)
    return {
      isValid: false,
      error: "Address must include a valid street type (St, Ave, Rd, etc.)",
    };

  return { isValid: true };
};

// Fix regex pattern without unnecessary escapes
export const sanitizeAddress = (address) => {
  return address.replace(/[#.,/&'"]/g, "");
};

// Update functions to use the response data
export const validatePostalCode = async (postalCode, country) => {
  try {
    if (!postalCode || !country) {
      if (process.env.NODE_ENV !== 'production') console.info("Missing required parameters for postal code validation");
      return false;
    }

    const response = await fetch(
      `${API_BASE_URL}/validate/postal-code/${encodeURIComponent(postalCode)}?country=${encodeURIComponent(country)}`,
    );

    if (!response.ok) {
      const errorText = await response.text();
      if (process.env.NODE_ENV !== 'production') console.info("Postal code validation server error:", errorText);
      return false;
    }

    const result = await response.json();
    return result.isValid;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.info("Error validating postal code:", error);
    return false;
  }
};

export const validateCity = (city, _countryCode) => {
  // Enhanced city validation with international support
  // Allow letters, spaces, hyphens, apostrophes, and periods
  const cityRegex = /^[a-zA-Z\s\-.']{2,}$/i;
  const isValid = cityRegex.test(city);

  return { isValid, error: isValid ? null : "Please enter a valid city name" };
};

// Format the postal / ZIP code as the user types so that the input feels
// responsive while still allowing them to enter all necessary characters.
// – US: keep digits and an optional trailing “-1234” segment.
// – CA: allow alphanumeric in the pattern "A1A 1A1" (space optional).
// – Fallback: return the value unchanged (converted to upper-case) so we do
//   not strip valid characters for other countries.
export const formatPostalCode = (postalCode, countryCode) => {
  if (!postalCode) return "";

  const code = (countryCode || "").toUpperCase();

  // United States – keep numbers and a single hyphen.
  if (code === "US") {
    return postalCode
      .replace(/[^0-9-]/g, "")
      .replace(/^(\d{5})-?(\d{0,4}).*/, (_, p1, p2) =>
        p2 ? `${p1}-${p2}` : p1,
      );
  }

  // Canada – letters & digits in alternating pattern, space after 3 chars.
  if (code === "CA") {
    // Remove anything that is not a letter or digit.
    const cleaned = postalCode.toUpperCase().replace(/[^A-Z0-9]/g, "");
    // Insert a space between the outward and inward code if possible.
    if (cleaned.length <= 3) return cleaned;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`.trim();
  }

  // Default – just uppercase the value so we do not inadvertently remove
  // valid characters for other locales.
  return postalCode.toUpperCase();
};

export const getStatesByCountry = (countryCode) => {
  switch (countryCode) {
    case "US":
      return US_STATES;
    case "CA":
      return CA_PROVINCES;
    case "GB":
      return UK_COUNTIES;
    case "AU":
      return AU_STATES;
    case "NZ":
      return NZ_REGIONS;
    // Returning an empty array for countries without predefined
    // sub-regions keeps the form functional without forcing the user to
    // select a value that does not exist.
    default:
      return [];
  }
};

export const getPostalCodePlaceholder = (countryCode) => {
  switch ((countryCode || "").toUpperCase()) {
    case "US":
      return "12345 or 12345-6789";
    case "CA":
      return "A1A 1A1";
    default:
      return "";
  }
};

// Updated USPS API configuration
const POSTAL_SERVICES = {
  US: {
    endpoint: config.usps.endpoint,
    credentials: {
      clientId: config.usps.consumerKey,
      clientSecret: config.usps.consumerSecret,
      scope: "addresses",
    },
  },
};

// Updated function to verify address with USPS
const verifyUSPSAddress = async (address) => {
  try {
    // Get OAuth token using token manager
    const token = await tokenManager.getToken(
      "USPS",
      POSTAL_SERVICES.US.credentials,
    );

    const params = new URLSearchParams({
      streetAddress: address.street,
      secondaryAddress: address.unit || "",
      city: address.city,
      state: address.state,
      ZIPCode: address.zipCode.split("-")[0],
      ZIPPlus4: address.zipCode.split("-")[1] || "",
    });

    const response = await fetch(
      `${POSTAL_SERVICES.US.endpoint}/address?${params}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      },
    );

    // Handle token expiration
    if (response.status === 401) {
      // Clear the invalid token
      tokenManager.clearToken("USPS", POSTAL_SERVICES.US.credentials.clientId);

      // Retry once with new token
      const newToken = await tokenManager.getToken(
        "USPS",
        POSTAL_SERVICES.US.credentials,
      );
      const retryResponse = await fetch(
        `${POSTAL_SERVICES.US.endpoint}/address?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${newToken}`,
            Accept: "application/json",
          },
        },
      );

      if (!retryResponse.ok) {
        throw new Error("Address verification failed after token refresh");
      }

      return await processUSPSResponse(retryResponse);
    }

    return await processUSPSResponse(response);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.info("USPS address verification error:", error);
    throw error;
  }
};

// Helper function to process USPS API response
const processUSPSResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json();

    // Handle specific error cases based on API response
    switch (response.status) {
      case 400:
        if (process.env.NODE_ENV !== 'production') console.info("Address validation error:", errorData.error.message);
        return { isValid: false, errors: { general: errorData.error.message } };
      case 404:
        if (process.env.NODE_ENV !== 'production') console.info("Address not found:", errorData.error.message);
        return {
          isValid: false,
          errors: { general: "Address could not be found or verified" },
        };
      case 429:
        if (process.env.NODE_ENV !== 'production') console.info(
          "Rate limit exceeded, retry after:",
          response.headers.get("Retry-After"),
        );
        throw new Error("Rate limit exceeded");
      default:
        throw new Error(
          errorData.error.message || "Address verification failed",
        );
    }
  }

  const data = await response.json();

  // Check for warnings
  if (data.warnings && data.warnings.length > 0) {
    if (process.env.NODE_ENV !== 'production') console.info("Address validation warnings:", data.warnings);
  }

  // Process additional information
  const additionalInfo = data.additionalInfo || {};
  const isDeliverable = additionalInfo.DPVConfirmation === "Y";

  // Return standardized address with validation info
  return {
    isValid: isDeliverable,
    standardizedAddress: {
      street:
        data.address.streetAddressAbbreviation || data.address.streetAddress,
      unit: data.address.secondaryAddress || "",
      city: data.address.cityAbbreviation || data.address.city,
      state: data.address.state,
      zipCode: `${data.address.ZIPCode}${data.address.ZIPPlus4 ? "-" + data.address.ZIPPlus4 : ""}`,
      country: "United States",
    },
    additionalInfo: {
      deliveryPoint: additionalInfo.deliveryPoint,
      carrierRoute: additionalInfo.carrierRoute,
      isBusiness: additionalInfo.business === "Y",
      isVacant: additionalInfo.vacant === "N",
      isCMRA: additionalInfo.DPVCMRA === "Y",
      isCentralDelivery: additionalInfo.centralDeliveryPoint === "Y",
    },
    corrections: data.corrections || [],
    matches: data.matches || [],
  };
};

// Updated verifyAddressWithPostalDB function
export const verifyAddressWithPostalDB = async (address) => {
  try {
    const countryCode = COUNTRIES.find((c) => c.name === address.country)?.code;
    if (!countryCode || countryCode !== "US") {
      if (process.env.NODE_ENV !== 'production') console.info("Only US addresses can be verified");
      return {
        isValid: false,
        errors: { general: "Only US addresses can be verified" },
      };
    }

    return await verifyUSPSAddress(address);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.info("Address verification error:", error);
    return {
      isValid: false,
      errors: { general: "Address verification failed" },
    };
  }
};

// Return an appropriate label for the postal/ZIP code field based on the
// supplied country. The argument can be either an ISO-3166-1 alpha-2 code or
// the country name that appears in the COUNTRIES list – AddressManager passes
// the country name at present.
export const getPostalCodeLabel = (country) => {
  // Normalise to upper-case for code comparison.
  const normalised = country?.toUpperCase?.() ?? "";

  if (country === "United States" || normalised === "US") {
    return "ZIP Code";
  }

  if (country === "United Kingdom" || normalised === "GB") {
    return "Postcode";
  }

  // Default for most other countries
  return "Postal Code";
};

export const validateAddress = async (address, countryCode) => {
  const errors = {};

  // Basic validation
  if (!address.street) errors.street = "Street address is required";
  if (!address.city) errors.city = "City is required";
  // State / Province validation – only require a value if the selected
  // country has any predefined sub-regions.  For many countries we do not
  // keep a list of states / provinces; in those cases <AddressManager>
  // hides the drop-down entirely and we must not mark the field as
  // required here either.

  const subRegions = getStatesByCountry(countryCode);
  if (subRegions.length > 0 && !address.state) {
    errors.state = "State is required";
  }
  if (!address.zipCode) errors.zipCode = "ZIP code is required";

  // ZIP code validation
  if (address.zipCode) {
    const isValid = await validatePostalCode(address.zipCode, countryCode);
    if (!isValid) {
      errors.zipCode = "Invalid ZIP code format";
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};
